import { Component, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DashboardService } from '../../_services/common/dashboard.service';
import { GetSetReminderConfigurationReqResp, ReminderConfiguration } from '../../_models/reminder-configuration';
import { ReminderoptionEnum, ReminderPanelTypeEnum } from '../../_models/enum/solo-enum';
import { IApiRequest } from '../../_models/common/request-response/api-request';
import { environment } from '../../../environments/environment';
import { Claims } from 'src/app/_models/common/claims';
import { ClaimsService } from 'src/app/_services/common/claims.service';
import { ToastrService } from 'ngx-toastr';
import { FlatpickrOptions } from 'ng2-flatpickr';
import { NotificationPanelService } from 'src/app/_services/notification-panel.service';

@Component({
    selector: 'org-setup-reminder-panel',
    templateUrl: './org-setup-reminder-panel.component.html',
    standalone: false
})
export class OrgSetupReminderPanelComponent implements OnInit, AfterViewInit {
  @Input() panelData: any;
  @Output() closePanelEvent = new EventEmitter<any>();
  @ViewChild("fReportDate", { static: false }) fReportDate: any;
  claims: Claims;
  // Form controls for the reminder options
  selectedReminderOption: ReminderoptionEnum = ReminderoptionEnum.EnterNumberOfDays;
  numberOfDays: number = 5;
  selectedDate: Date = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000);
  
  // Enum references for template
  ReminderoptionEnum = ReminderoptionEnum;
   datePickerOptions: FlatpickrOptions = {
    mode: "single",
    defaultDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    minDate: new Date(Date.now() + 24 * 60 * 60 * 1000), // Tomorrow's date
    altInput: true,
    altFormat: "d-M-Y",
    dateFormat: "d-M-Y",
  };
  
  constructor(
    private translate: TranslateService,
    private router: Router,
    private dashboardService: DashboardService,
     private claimsService: ClaimsService,
     private toastr: ToastrService,
     private cdr: ChangeDetectorRef,
     private notificationPanelService: NotificationPanelService
  ) {
    this.claims = new Claims();
    this.claimsService.currentClaims.subscribe((claims) => {
      this.claims = claims;
      
    });
  }
  
  ngOnInit(){
    // Don't initialize flatpickr here as ViewChild is not ready yet
    // Don't load existing configuration here as we need the view to be ready
  }
  
  ngAfterViewInit() {
    // Initialize flatpickr after view is ready
    if (this.fReportDate && this.fReportDate.nativeElement) {
      this.fReportDate.nativeElement.flatpickr(this.datePickerOptions);
    }
    this.onReminderOptionChange(ReminderoptionEnum.SelectDate);
    // Ensure default selection is properly applied and force change detection
    setTimeout(() => {
      this.onReminderOptionChange(ReminderoptionEnum.EnterNumberOfDays);
      this.cdr.detectChanges(); // Force change detection to ensure radio button is visually selected
    }, 50);
  }
  
  onCloseClick(){
    let closemodel = {
      isClose: true,
      isReminderSent: false
    }
    this.closePanelEvent.emit(closemodel);
  }
  
  onClick(){
    this.claims.ShowOrgSetupReminderPanel = false;
    this.claimsService.updateClaims(this.claims);
    
    // Close the notification panel before redirecting
    this.notificationPanelService.closeNotificationPanel();
    
    this.router.navigate(["setupyourorganisation"]);
  }
  
  onReminderOptionChange(option: ReminderoptionEnum) {
    this.selectedReminderOption = option;
  }
  
  onDaysChange(days: any) {
    this.numberOfDays = parseInt(days, 10);
  }
  
  onDateChange(date: any) {
    // Ensure we have a proper Date object
    if (date) {
      this.selectedDate = new Date(date);
    }
  }

  // Check if save button should be enabled
  get isSaveButtonEnabled(): boolean {
    return true; // Always enabled since we have a default selection
  }
  
  saveReminderConfiguration() {
    try {
      let reminderStartDate: number = 0;
      
      // Calculate reminder start date based on selected option
      switch (this.selectedReminderOption) {
        case ReminderoptionEnum.EnterNumberOfDays:
          // Add specified number of days to current date
          const futureDate = new Date();
          futureDate.setDate(futureDate.getDate() + this.numberOfDays);
          // Set to start of day in local time zone
          futureDate.setHours(0, 0, 0, 0);
          reminderStartDate = futureDate.getTime();
          break;
          
        case ReminderoptionEnum.SelectDate:
          // Use the selected date - ensure it's a proper Date object in local time zone
          if (this.selectedDate) {
            const dateObj = new Date(this.selectedDate);
            // Set to start of day in local time zone
            dateObj.setHours(0, 0, 0, 0);
            reminderStartDate = dateObj.getTime();
          } else {
            const defaultDate = new Date();
            defaultDate.setHours(0, 0, 0, 0);
            reminderStartDate = defaultDate.getTime();
          }
          break;
          
        case ReminderoptionEnum.NoReminder:
          // No reminder - set to 0 or current date
          reminderStartDate = 0;
          break;
      }
      
      // Create reminder configuration object
      const reminderConfig = new ReminderConfiguration();
      reminderConfig.reminderType = this.selectedReminderOption;
      reminderConfig.reminderPanelType = ReminderPanelTypeEnum.OrgSetup; // Using OrgSetup enum value
      reminderConfig.reminderStartDate = reminderStartDate;
      reminderConfig.reminderSetDate = new Date().getTime();
      reminderConfig.reminderSetByUser = this.claims.UserId;
      reminderConfig.orgId = this.claims.OrgId;
      
      // Create request object
      const requestData = new GetSetReminderConfigurationReqResp();
      requestData.reminderConfigurations = [reminderConfig];
      requestData.action = 2; // 2 for save/set action
      requestData.orgId = this.claims.OrgId;
      requestData.reminderPanelType = ReminderPanelTypeEnum.OrgSetup;
      
      // Call the service
      this.dashboardService.GetSetReminderConfiguration(requestData).subscribe({
        next: (response) => {
          if (response && response.statusCode === 200) {
            console.log('Organization setup reminder configuration saved successfully');
            
            // Create dynamic success message based on selected option
            let successMessage = '';
            switch (this.selectedReminderOption) {
              case ReminderoptionEnum.EnterNumberOfDays:
                successMessage = this.translate.instant("reminder_after_days_message", { days: this.numberOfDays });
                break;
              case ReminderoptionEnum.SelectDate:
                const formattedDate = this.selectedDate.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                });
                successMessage = this.translate.instant("reminder_on_date_message", { date: formattedDate });
                break;
              case ReminderoptionEnum.NoReminder:
                successMessage = this.translate.instant("no_reminder_message");
                break;
              default:
                successMessage = this.translate.instant("reminder_sent_message");
                break;
            }
            
            // Show success toaster message
            this.toastr.success(successMessage, this.translate.instant("label_success"));
            let closemodel = {
                isClose: false,
                isReminderSent: true
              };
            this.closePanelEvent.emit(closemodel);
          } else {
            console.error('Failed to save organization setup reminder configuration:', response.message);
            // Show error message to user
            this.toastr.error(this.translate.instant("failed_to_save_reminder_config"), this.translate.instant("label_error"));
          }
        },
        error: (error) => {
          console.error('Error saving organization setup reminder configuration:', error);
          // Show error message to user
          this.toastr.error(this.translate.instant("error_saving_reminder_config"), this.translate.instant("label_error"));
        }
      });
      
    } catch (error) {
      console.error('Error in saveReminderConfiguration:', error);
    }
  }
  
  loadExistingConfiguration() {
    try {
      let requestData = new GetSetReminderConfigurationReqResp();
      requestData.action = 1; // 1 for get/load action
      requestData.orgId = this.claims.OrgId;
      requestData.reminderPanelType = ReminderPanelTypeEnum.OrgSetup;
      
      this.dashboardService.GetSetReminderConfiguration(requestData).subscribe({
        next: (response) => {
          if (response && response.responseInfo && response.responseInfo.reminderConfigurations 
            && response.responseInfo.reminderConfigurations.length > 0) {
            let config = response.responseInfo.reminderConfigurations[0];
            this.selectedReminderOption = config.reminderType;
            
            if (config.reminderStartDate > 0) {
              this.selectedDate = new Date(config.reminderStartDate);
              
              // Update datePickerOptions with date coming from server
              this.datePickerOptions.defaultDate = this.selectedDate;
              
              // Reinitialize flatpickr with updated options
              if (this.fReportDate && this.fReportDate.nativeElement) {
                this.fReportDate.nativeElement.flatpickr(this.datePickerOptions);
              }
              
              // Calculate number of days if it was set using days option
              if (this.selectedReminderOption === ReminderoptionEnum.EnterNumberOfDays) {
                let diffTime = Math.abs(this.selectedDate.getTime() - new Date().getTime());
                this.numberOfDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              }
            }
          }
          else
          {
            // No existing configuration found - keep default selection
            // Ensure default selection is maintained
            this.selectedReminderOption = ReminderoptionEnum.EnterNumberOfDays;
            this.numberOfDays = 5;
            
            // Initialize flatpickr if no configuration found and view is ready
            if (this.fReportDate && this.fReportDate.nativeElement) {
              this.fReportDate.nativeElement.flatpickr(this.datePickerOptions);
            }
            
            // Force change detection to ensure radio button shows as selected
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          console.error('Error loading organization setup reminder configuration:', error);
        }
      });
    } catch (error) {
      console.error('Error in loadExistingConfiguration:', error);
    }
  }
}
